import { camelCase } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Notification } from '@/component';
import { useFrameSubmitQuery, useVerfiyModal } from '@/hook';
import useRejectModal from '@/hook/frame/useRejectModal';
import type { VStatusBadgeProps } from '@/package';
import {
  AccountCircle,
  Send,
  Undo,
  VButton,
  VFlex,
  VStatusBadge,
  Verified,
  cn,
} from '@/package';
import {
  useAuthStore,
  // useLabelingBoxStore,
  useTaskFrameStore,
} from '@/store';
import { convertUserEmailToHexColor, getFrameState } from '@/util';

import { TaskLayoutHeaderWorkStateRevertButton } from './TaskLayoutHeaderWorkStateRevertButton';

const TaskLayoutHeaderOperation = () => {
  const { t } = useTranslation();
  const {
    currentFrame,
    isFrameDataLoading,
    selectedTask,
    isAssignedMe,
    isAssignedReviewMe,
  } = useTaskFrameStore();
  const { user } = useAuthStore();
  const { mutateAsync: submit } = useFrameSubmitQuery();
  const taskId = useMemo(() => selectedTask?.id, [selectedTask]);
  const { frameSeq } = useParams();
  const { openVerifyModal } = useVerfiyModal({
    frameSeq: +frameSeq!,
    taskId: +taskId!,
  });
  const { openRejectModal } = useRejectModal({
    frameSeq: +frameSeq!,
    taskId: +taskId!,
  });

  const isManager = user.role === 'Manager' || user.role === 'SiteAdmin';
  const isWorker = user.role === 'Worker';

  const isMyWork = isAssignedMe();
  const isMyReview = isAssignedReviewMe();

  const status =
    selectedTask && currentFrame
      ? getFrameState(currentFrame, selectedTask)
      : 'Not started';

  const isInProgress = status === 'In progress';
  const isInReview = status === 'In review';
  const isRejected = status === 'Rejected';

  const isSubmitable =
    (isManager && (isInProgress || isRejected)) ||
    (isWorker && isMyWork && (isInProgress || isInReview));

  const onSubmit = () => {
    if (isFrameDataLoading) return Promise.resolve();

    return submit({
      pTaskID: +taskId!,
      pFrameSeq: currentFrame?.frameSeq ?? 1,
    });
  };

  return (
    <VFlex gap={1} items="center">
      <VFlex items="center" gap={0.5}>
        <div className="rounded-full border border-gray-200 p-1">
          <VFlex className="gap-[2px]" items="center">
            {status && (
              <VStatusBadge
                variant={
                  status === 'View Only'
                    ? 'notStarted'
                    : (camelCase(
                        status.toLowerCase(),
                      ) as VStatusBadgeProps['variant'])
                }
              >
                {status}
              </VStatusBadge>
            )}

            <TaskLayoutHeaderWorkStateRevertButton />
          </VFlex>
        </div>

        <div className="h-4 w-px bg-gray-300" />

        <VFlex
          gap={0.25}
          className={cn('items-center rounded-full px-2 py-1', {
            'border border-gray-200 bg-white': isMyWork,
            'bg-gray-400': !isMyWork,
          })}
          items="center"
        >
          <div
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full',
            )}
            style={{
              backgroundColor: isMyWork
                ? convertUserEmailToHexColor(user.email).backgroundColor
                : 'inherit',
              color: isMyWork
                ? convertUserEmailToHexColor(user.email).color
                : 'inherit',
            }}
          >
            {!isMyWork && <AccountCircle className="size-full" />}
            {isMyWork && (
              <div className="text-body-md text-black">{user.email[0]}</div>
            )}
          </div>
          <div className="text-body-md text-black">
            {isMyWork ? 'My' : 'Other'}
          </div>
        </VFlex>
      </VFlex>

      <Notification iconSize="md" />

      {isSubmitable && (
        <VButton size="md" onClick={onSubmit}>
          <Send />
          {t('frame.taskFrameSubmitButton.respectless')}
        </VButton>
      )}

      {isInReview && isMyReview && (
        <VFlex gap={0.5} items="center">
          <VButton size="md" variant="dangerOutline" onClick={openRejectModal}>
            <Undo />
            {t('review.rejectButtonText.pioneership')}
          </VButton>

          <VButton
            size="md"
            onClick={() => {
              if (isFrameDataLoading) return;
              openVerifyModal();
            }}
          >
            <Verified />
            {t('frame.verify.artless')}
          </VButton>
        </VFlex>
      )}
    </VFlex>
  );
};

export default TaskLayoutHeaderOperation;
